import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import badge768Avif from "../assets/logo/badge-768.avif";
import badge768Webp from "../assets/logo/badge-768.webp";
import badge1024Avif from "../assets/logo/badge-1024.avif";
import badge1024Webp from "../assets/logo/badge-1024.webp";
import badge1280Avif from "../assets/logo/badge-1280.avif";
import badge1280Webp from "../assets/logo/badge-1280.webp";
import { LeagueBadge } from "./LeagueBadge";

// Built from assets-src/logo.jpg by `make logo` (build-logo.mjs). AVIF is
// about half WebP's size; WebP covers browsers without AVIF (pre-16.4 iOS).
const FULL_SRCSET_AVIF = `${badge768Avif} 768w, ${badge1024Avif} 1024w, ${badge1280Avif} 1280w`;
const FULL_SRCSET_WEBP = `${badge768Webp} 768w, ${badge1024Webp} 1024w, ${badge1280Webp} 1280w`;
// The image's rendered width, which the browser multiplies by the screen's
// pixel density to pick a file from the srcsets. It's a square capped at
// 640px inside the dialog's 16px padding (.logo-lightbox__image), so it's
// limited by whichever is shorter: the height on a landscape screen less
// than 672px tall, otherwise the cap or the width.
const FULL_SIZES = [
  "(orientation: landscape) and (max-height: 671px) calc(100vh - 32px)",
  "(min-width: 672px) 640px",
  "calc(100vw - 32px)",
].join(", ");

interface LogoLightboxProps {
  /** Names the badge in the enlarged view's alt text and dialog label. */
  leagueName: string;
}

/**
 * The header's league badge, which opens full-screen when tapped so it can
 * be seen at a readable size on a phone. Only reachable below 1024px — the
 * button is CSS-hidden at desktop widths, where the badge instead sits beside
 * the title as a home link (see .layout__brand in app.css and Layout.tsx).
 *
 * Uses a native modal <dialog>: showModal() puts it in the top layer (so the
 * sticky header's backdrop-filter doesn't trap it), makes the page behind it
 * inert, closes on Escape, and returns focus to the badge on close.
 */
export function LogoLightbox({ leagueName }: LogoLightboxProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  // The full-size image (up to ~60KB) is only rendered, and so only
  // downloaded, once someone first opens the dialog; it then stays mounted
  // so reopening is instant.
  const [hasOpened, setHasOpened] = useState(false);

  // A modal dialog doesn't stop the page behind it scrolling under a finger
  // on touch devices, so lock it the same way the nav drawer does.
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // The badge is CSS-hidden at >=1024px (see app.css). If the viewport
  // crosses that breakpoint while the dialog is open (a resize or rotation),
  // close it rather than leave it up over a page with no visible trigger to
  // return focus to — the same rule useNavDrawer applies to the drawer.
  useEffect(() => {
    if (!isOpen) return;
    const query = window.matchMedia("(min-width: 1024px)");
    function handleChange(e: MediaQueryListEvent) {
      if (e.matches) dialogRef.current?.close();
    }
    query.addEventListener("change", handleChange);
    return () => query.removeEventListener("change", handleChange);
  }, [isOpen]);

  function open() {
    const dialog = dialogRef.current;
    if (!dialog) return;
    dialog.showModal();
    // Focus the dialog itself (tabIndex=-1) rather than letting showModal()
    // pick the close button, which mobile Safari would paint with a stray
    // focus ring after a tap. Same reasoning as useNavDrawer's panel focus.
    dialog.focus();
    setIsOpen(true);
    setHasOpened(true);
  }

  function close() {
    dialogRef.current?.close();
  }

  return (
    <>
      <button
        type="button"
        className="layout__logo-button"
        aria-label={`Enlarge the ${leagueName} logo`}
        aria-haspopup="dialog"
        onClick={open}
      >
        <LeagueBadge className="layout__logo" size={44} />
      </button>

      {/* A tap anywhere (badge or backdrop) closes it, which is what people
          expect from a photo viewer; the close button is there for
          keyboard and screen-reader users and closes via the same click.
          onClose covers every path out, Escape included. */}
      <dialog
        ref={dialogRef}
        className="logo-lightbox"
        aria-label={`${leagueName} logo`}
        tabIndex={-1}
        onClick={close}
        onClose={() => setIsOpen(false)}
      >
        <button type="button" className="logo-lightbox__close" aria-label="Close">
          <X aria-hidden="true" size={22} />
        </button>
        {hasOpened && (
          <picture className="logo-picture">
            <source type="image/avif" srcSet={FULL_SRCSET_AVIF} sizes={FULL_SIZES} />
            <img
              className="logo-lightbox__image"
              src={badge1024Webp}
              srcSet={FULL_SRCSET_WEBP}
              sizes={FULL_SIZES}
              alt={`${leagueName} logo`}
              width={1280}
              height={1280}
              decoding="async"
            />
          </picture>
        )}
      </dialog>
    </>
  );
}
